import React, { useEffect, useRef, useState } from 'react'
import { Button, CircularProgress, Modal } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import useFormStyles from '../common/FormFields/FormFieldsStyle'
import ApartmentAssignmentList from './ApartmentAssignmentList'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import useUsersApi from '../../hooks/users/useUsersApi'
import useToastContext from '../../hooks/useToastContext'
import { notificationTypes } from '../../contexts/toastContext'

export default function ManageApartmentsModal({
  open,
  onClose,
  onSave,
  user,
  building,
  buildings,
  initialAssignments,
}) {
  const classes = useStyles()
  const formClasses = useFormStyles()
  const addToast = useToastContext()

  const [{ buildingsState }, { getBuildings }] = useBuildingsApi()
  const [, { getUserApartments, updateUserApartments }] = useUsersApi()

  const [assignments, setAssignments] = useState(
    initialAssignments || [{ building: null, apartment: null }]
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [fetchFailed, setFetchFailed] = useState(false)
  const savedBuildings = useRef([])

  useEffect(() => {
    if (!open || !user?.id) return

    // Always the full list from the API: what the parent holds can be filtered
    // by building, and saving a partial list removes the rest.
    setIsFetching(true)
    setFetchFailed(false)
    if (!buildings) getBuildings()
    getUserApartments(user.id)
      .then((apartments) => {
        savedBuildings.current = (apartments || [])
          .map((apt) => apt.building)
          .filter(Boolean)
        if (apartments && apartments.length > 0) {
          setAssignments(
            apartments.map((apt) => ({
              building: apt.building || null,
              apartment: apt,
            }))
          )
        } else if (building) {
          setAssignments([{ building, apartment: null }])
        } else {
          setAssignments([{ building: null, apartment: null }])
        }
      })
      .catch(() => {
        setFetchFailed(true)
        addToast(notificationTypes.error, 'Error al cargar las unidades del usuario')
      })
      .finally(() => setIsFetching(false))
  }, [open, user?.id])

  const handleSave = async () => {
    const validAssignments = assignments.filter((a) => a.apartment)
    const apartmentIds = validAssignments.map((a) => a.apartment.id)
    const keptBuildingIds = validAssignments.map((a) => a.building?.id)
    const removedBuildings = Array.from(
      new Map(
        savedBuildings.current
          .filter((b) => !keptBuildingIds.includes(b.id))
          .map((b) => [b.id, b.name])
      ).values()
    )
    if (
      removedBuildings.length > 0 &&
      !window.confirm(
        `Al quitar a ${user.name} de ${removedBuildings.join(', ')} se eliminarán sus invitaciones, PINs, eventos, reservas y notificaciones en ese edificio. Esta acción no se puede deshacer. ¿Desea continuar?`
      )
    ) {
      return
    }
    setIsLoading(true)
    try {
      await updateUserApartments(user.id, apartmentIds)
      addToast(notificationTypes.success, 'Unidades actualizadas con éxito!')
      onSave && onSave(validAssignments)
      onClose()
    } catch (error) {
      addToast(
        notificationTypes.error,
        `Error al actualizar las unidades: ${error?.response?.data?.message || error.message}`
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <div className={classes.modal}>
        <h2 className={classes.title}>Gestionar unidades</h2>

        {isFetching ? (
          <div className={classes.spinnerContainer}>
            <CircularProgress />
          </div>
        ) : (
          <ApartmentAssignmentList
            assignments={assignments}
            onChange={setAssignments}
            buildings={buildings || buildingsState.buildings}
            disableBuilding={!!building}
          />
        )}

        <div className={classes.actions}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            className={formClasses.secondaryButton}
            style={{ marginRight: '10px' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isLoading || fetchFailed}
            className={formClasses.primaryButton}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Guardar'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const useStyles = makeStyles((theme) => ({
  modal: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3, 4, 3),
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    position: 'fixed',
    minWidth: 560,
    maxWidth: '90vw',
    maxHeight: '80vh',
    overflowY: 'auto',
    borderRadius: 4,
  },
  title: {
    marginTop: 0,
    marginBottom: theme.spacing(3),
  },
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
}))
